import { AppStateInitializer } from '@components/DataInitializer';
import { AuthSidebar } from '@components/layouts';
import { OrgRedirect } from '@components/OrgRedirect';
import { ProgramRouteValidator } from '@components/ProgramRouteValidator';
import { PublicRoute } from '@components/PublicRoute';
import { RedirectToSelectedOrg } from '@components/RedirectToSelectedOrg';
import { AgentDetailPage, AnalyticsContainer, QuestionDetailPage } from '@pages/analytics';
import {
  CompleteSignupPage,
  LoginForm,
  LogoutButton,
  ResetPasswordPage,
  SignupPage,
  SSOCallback,
  VerifyEmailPage,
} from '@pages/auth';
import { CallsContainer } from '@pages/calls/CallsContainer';
import { CasesRouter, CreateCase } from '@pages/cases';
import { CasesAnalyticsContainer } from '@pages/cases-analytics';
import * as Sentry from '@sentry/react';
import { useStore } from '@store/index';
import React, { useEffect } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { AnalyticsRouteValidator } from '@/components/AnalyticsRouteValidator';
import { CallsRouteValidator } from '@/components/CallsRouteValidator';
import { ErrorContainer } from '@/components/ErrorContainer';
import { Header } from '@/components/Header';
import { OrgRouteValidator } from '@/components/OrgRouteValidator';
import { QcRouteValidator } from '@/components/QcRouteValidator';
import { WebhooksRouteValidator } from '@/components/WebhooksRouteValidator';
import { useShouldShowAnalyticsPage } from '@/hooks/useShouldShowAnalyticsPage';
import { useShouldShowCasesAnalyticsPage } from '@/hooks/useShouldShowCasesAnalyticsPage';
import { AllTasksAccessGate, AllTasksContainer } from '@/pages/all-tasks';
import { CampaignDetails } from '@/pages/campaigns/CampaignDetails';
import { CampaignsContainer } from '@/pages/campaigns/CampaignsContainer';
import { CaseDetailsPage } from '@/pages/cases/CaseDetailsPage';
import { CallConsolePage } from '@/pages/cases/console/CallConsolePage';
import { ChatContainer } from '@/pages/chat';
import { MailboxContainer } from '@/pages/mailbox';
import { MyCasesContainer } from '@/pages/my-cases';
import { MyTasksContainer, MyTasksRouteValidator } from '@/pages/my-tasks';
import { QcContainer, QcDetailPage } from '@/pages/qc';
import { buildQcListPath } from '@/pages/qc/utils';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { WebhookEndpointDetailPage, WebhooksContainer } from '@/pages/webhooks';
import { WorkforceAccessGate, WorkforceContainer } from '@/pages/workforce';
import { SidebarStateEnum } from '@/types/sidebar';
import type { ApiErrorWithCode } from '@/utils/errorCodes';
import { removeOrgPrefix } from '@/utils/routes';

// Error fallback component
const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  // Note: react-error-boundary@6.x passes unknown type for error
  const errorWithCode = error as ApiErrorWithCode;
  const errorMessage =
    errorWithCode?.userMessage ||
    (error instanceof Error ? `Error: ${error.name}` : 'An unexpected error occurred');

  return (
    <div className="w-full h-full flex flex-col justify-center items-center p-8">
      <div className="max-w-md w-full">
        <ErrorContainer
          errorMessage={errorMessage}
          buttonText="Try Again"
          buttonOnClick={resetErrorBoundary}
        />
      </div>
    </div>
  );
};

const QcErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const navigate = useNavigate();
  const { selectedOrganizationId } = useStore();
  const { orgId } = useParams<{ orgId?: string }>();
  const routeOrganizationId = orgId ? removeOrgPrefix(orgId) : null;
  const qcListPath = buildQcListPath(routeOrganizationId, selectedOrganizationId);
  const errorWithCode = error as ApiErrorWithCode;
  const errorMessage =
    errorWithCode?.userMessage ||
    (error instanceof Error ? `Error: ${error.name}` : 'An unexpected error occurred');

  return (
    <div className="w-full h-full flex flex-col justify-center items-center p-8">
      <div className="max-w-md w-full">
        <ErrorContainer
          errorMessage={errorMessage}
          buttonText="Go Home"
          buttonOnClick={() => {
            resetErrorBoundary();
            navigate(qcListPath, { replace: true });
          }}
        />
      </div>
    </div>
  );
};

const QcRouteLayout = () => {
  const location = useLocation();

  return (
    <ErrorBoundary
      FallbackComponent={QcErrorFallback}
      resetKeys={[location.pathname, location.search]}
      onError={(error, errorInfo) => {
        Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: errorInfo.componentStack,
            },
          },
        });
      }}
    >
      <Outlet />
    </ErrorBoundary>
  );
};

// Layout component for protected routes with sidebar
const ProtectedLayout = () => {
  const { isAuthenticated, sidebarState, setSidebarState } = useStore();
  const location = useLocation();

  // For simplicity, we always want the sidebar expanded
  const desiredSidebarState = SidebarStateEnum.EXPANDED;

  useEffect(() => {
    // Ensure sidebar is in the desired state
    if (sidebarState !== desiredSidebarState) {
      setSidebarState(desiredSidebarState);
    }
  }, [sidebarState, desiredSidebarState, setSidebarState]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <AppStateInitializer>
      <Outlet />
    </AppStateInitializer>
  );
};

// Layout component with Header - rendered inside org/prog context
const ProgramLayout = () => {
  const location = useLocation();

  return (
    <>
      <Header />
      <AuthSidebar>
        {/* QC and the other routed pages inherit this shared render-time error boundary here,
            so the QC page does not need a separate nested boundary unless it introduces a more
            specific recovery experience later. */}
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          resetKeys={[location.pathname]}
          onError={(error, errorInfo) => {
            // Report to Sentry with component stack trace
            Sentry.captureException(error, {
              contexts: {
                react: {
                  componentStack: errorInfo.componentStack,
                },
              },
            });
          }}
        >
          <Outlet />
        </ErrorBoundary>
      </AuthSidebar>
    </>
  );
};

const RoutesConfig = () => {
  const { isAuthenticated } = useStore();

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="login"
        element={
          <PublicRoute>
            <LoginForm />
          </PublicRoute>
        }
      />

      <Route
        path="signup"
        element={
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        }
      />

      <Route
        path="complete-signup"
        element={
          <PublicRoute>
            <CompleteSignupPage />
          </PublicRoute>
        }
      />

      <Route
        path="reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />

      <Route
        path="verify-email"
        element={
          <PublicRoute>
            <VerifyEmailPage />
          </PublicRoute>
        }
      />

      <Route
        path="sso/callback/:connectionId"
        element={
          <PublicRoute>
            <SSOCallback />
          </PublicRoute>
        }
      />
      {/* Deprecated alias — kept while old IdP redirect_uri registrations roll over */}
      <Route
        path="auth/callback/:connectionId"
        element={
          <PublicRoute>
            <SSOCallback />
          </PublicRoute>
        }
      />

      <Route path="logout" element={<LogoutButton />} />

      {/* Protected Routes with nested structure */}
      <Route element={<ProtectedLayout />}>
        {/* Root - redirect to selected org/prog cases */}
        <Route index element={<RedirectToSelectedOrg />} />
        {/* Organization routes with validation */}
        <Route path=":orgId" element={<OrgRouteValidator />}>
          {/* Handle org-only URL - redirect to first program */}
          <Route index element={<OrgRedirect />} />

          <Route element={<ProgramLayout />}>
            {/* QC is scoped at the organization level, so it lives outside :progId routes but still
                reuses the shared app chrome and its own route validator. */}
            <Route element={<QcRouteValidator />}>
              <Route element={<QcRouteLayout />}>
                <Route path="qc" element={<QcContainer />} />
                <Route path="qc/:evaluationId" element={<QcDetailPage />} />
              </Route>
            </Route>
            {/* Analytics is org-scoped like QC (no :progId). The validator
                enforces the same role/permission gate as the sidebar entry
                so direct/bookmarked URLs can't bypass it. */}
            <Route element={<AnalyticsRouteValidator useAccess={useShouldShowAnalyticsPage} />}>
              <Route path="analytics" element={<AnalyticsContainer />} />
              <Route path="analytics/agents/:agentId" element={<AgentDetailPage />} />
              <Route path="analytics/questions/:metricKey" element={<QuestionDetailPage />} />
            </Route>
            {/* AE / PQC Analytics: a sibling of QC analytics (not nested under /analytics, so the
                sidebar doesn't light up both entries), gated by the case_analytics permission. */}
            <Route
              element={<AnalyticsRouteValidator useAccess={useShouldShowCasesAnalyticsPage} />}
            >
              <Route path="cases-analytics" element={<CasesAnalyticsContainer />} />
            </Route>
            {/* Webhooks — org-scoped (endpoints + deliveries for one organization).
                Gated like the sidebar entry: per-org feature flag webhooks_<org_id>
                plus the ListWebhookEndpoints permission (Organization Admin). */}
            <Route element={<WebhooksRouteValidator />}>
              <Route path="webhooks" element={<WebhooksContainer />} />
              <Route
                path="webhooks/endpoints/:endpointId"
                element={<WebhookEndpointDetailPage />}
              />
            </Route>
            {/* Workforce Console — org-scoped, gated like the sidebar entry
                (WorkforceAccessGate): team leads, or with the
                permissions_based_wc_access flag on, the permission map, the
                PQC rule and the hide_workforce_console_<org_id> blocklist.
                Lives outside :progId because it has its own program filter and
                shouldn't be tied to the active program selector. */}
            <Route
              path="workforce"
              element={
                <WorkforceAccessGate>
                  <WorkforceContainer />
                </WorkforceAccessGate>
              }
            />
            {/* My Cases — org-scoped: lists cases assigned to the current user
                across all programs in the organization. */}
            <Route path="my-cases" element={<MyCasesContainer />} />
            {/* My Tasks — org-scoped: manual tasks assigned to the current user
                across all programs in the organization. Only for orgs in
                REACT_APP_TASKS_ENABLED_ORG_IDS. */}
            <Route element={<MyTasksRouteValidator />}>
              <Route path="my-tasks" element={<MyTasksContainer />} />
            </Route>
            {/* All Tasks — org-scoped: every assignee's manual tasks across the
                programs the user may read. Gated like its sidebar entry
                (AllTasksAccessGate): REACT_APP_ALL_TASKS_ENABLED_ORG_IDS, then team
                leads or, with permissions_based_all_tasks_access on, the roles
                allowed into the Workforce Console. */}
            <Route
              path="all-tasks"
              element={
                <AllTasksAccessGate>
                  <AllTasksContainer />
                </AllTasksAccessGate>
              }
            />
            {/* Cases — org-scoped entry for Tier-2 users (case_record:write),
                whose cases list spans every program in the org. CasesRouter
                resolves tier: tier2 → Tier2CasesContainer (org-wide); any other
                user hitting this route is redirected to their program cases
                route (CasesContainer requires a selected program). The
                program-scoped /cases route below remains for standard users. */}
            <Route path="cases" element={<CasesRouter />} />
          </Route>

          {/* Program routes with validation */}
          <Route path=":progId" element={<ProgramRouteValidator />}>
            {/* Wrap all program routes with ProgramLayout to include Header */}
            <Route element={<ProgramLayout />}>
              {/* Cases routes */}
              <Route path="cases">
                <Route index element={<CasesRouter />} />
                <Route path=":caseId" element={<CaseDetailsPage />} />
                <Route path=":caseId/calls/:callId/console" element={<CallConsolePage />} />
              </Route>
              {/* Create case route */}
              <Route path="create-case" element={<CreateCase />} />
              <Route path="campaigns" element={<CampaignsContainer />} />
              <Route path="campaigns/:campaignId" element={<CampaignDetails />} />
              {/* Calls route - protected by CallsRouteValidator */}
              <Route element={<CallsRouteValidator />}>
                <Route path="calls" element={<CallsContainer />} />
              </Route>
              {/* Mailbox route */}
              <Route path="mailbox" element={<MailboxContainer />} />
              {/* AI Chat route */}
              <Route path="chat" element={<ChatContainer />} />
              {/* Settings route */}
              <Route path="settings" element={<SettingsPage />} />
            </Route>
          </Route>
        </Route>
        {/* Fallback if no route matches */}
        <Route path="*" element={<Navigate to={isAuthenticated ? '/' : '/login'} replace />} />
      </Route>
    </Routes>
  );
};

export { RoutesConfig };
